import { FC, useState } from 'react';
import MenuButton from './MenuButton';
import { Drawer, drawerClasses } from '@o2/ui';
import SidebarMenu, { SidebarMenuProps } from '../SidebarMenu';
import { styled } from '@mui/material';

export interface MobileMenuProps extends Omit<SidebarMenuProps, 'onClick' | 'onKeyDown'> {
  /**
   * ID of the MenuButton component (hamburger), propagated to the DOM as `data-elm`.
   */
  buttonElmId?: string;

  /**
   * ID of the Drawer component, propagated to the DOM as `data-elm`.
   */
  drawerElmId?: string;

  /**
   * Z-index to use for the Drawer inside. Defaults to MUI's default (https://mui.com/material-ui/customization/z-index/),
   * which is 1200 as of 11/2022.
   */
  drawerZIndex?: number;
}

const StyledDrawer = styled(Drawer, {
  name: 'O2Drawer',
  slot: 'root',
})(({ theme }) => ({
  [`& .${drawerClasses.paper}`]: {
    backgroundColor: theme.palette.background.white,
  },
}));

/**
 * MobileMenu is pre-implemented Menu button and Drawer with SidebarMenu. On click Menu button, SidebarMenu automatically show.
 */
const MobileMenu: FC<MobileMenuProps> = ({ buttonElmId, drawerElmId, drawerZIndex, children, logo }) => {
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  return (
    <>
      <MenuButton data-elm={buttonElmId} onClick={() => setOpen(true)} />
      <StyledDrawer
        data-elm={drawerElmId}
        open={open}
        anchor="left"
        onClose={handleClose}
        sx={
          /*
           * yes, it has to be written like this, passing {zIndex:drawerZIndex} even when undefined causes an unset of Drawer's
           * default value and it gets reset to Modal's default (and that, as of 11/2022, is 1300 instead of the expected 1200).
           * see https://mui.com/material-ui/customization/z-index/
           */
          drawerZIndex ? { zIndex: drawerZIndex } : undefined
        }
      >
        <SidebarMenu logo={logo} onClick={handleClose} onKeyDown={handleClose}>
          {children}
        </SidebarMenu>
      </StyledDrawer>
    </>
  );
};

MobileMenu.displayName = 'AppBar.MobileMenu';

export default MobileMenu;
